<script setup lang="ts">
import { ChevronLeft, Search } from 'lucide-vue-next'
import { nextTick, useTemplateRef, watch } from 'vue'
import type { Props } from './type'

defineOptions({
  name: 'SearchInputBar',
})

const props = withDefaults(defineProps<Props>(), {
  searched: false,
  placeholder: '搜索角色、用户、作品、话题',
  autofocus: false,
})

const emit = defineEmits<{
  'update:modelValue': [value: string]
  submit: [value: string]
  back: []
  clear: []
}>()

const inputElement = useTemplateRef<HTMLInputElement>('inputElement')

function updateValue(event: Event) {
  const target = event.target as HTMLInputElement

  emit('update:modelValue', target.value)
}

function handleSubmit() {
  emit('submit', props.modelValue)
}

watch(
  () => props.autofocus,
  (enabled) => {
    if (enabled) {
      void nextTick(() => inputElement.value?.focus())
    }
  },
  { immediate: true },
)
</script>

<template>
  <form class="search-input" :class="{ 'is-searched': searched }" role="search" @submit.prevent="handleSubmit">
    <button
      v-if="searched"
      type="button"
      class="search-input__back"
      aria-label="返回搜索首页"
      @click="emit('back')"
    >
      <ChevronLeft :size="24" stroke-width="1.45" aria-hidden="true" />
    </button>

    <div class="search-input__box">
      <input
        ref="inputElement"
        class="search-input__field"
        type="search"
        :value="modelValue"
        :placeholder="placeholder"
        enterkeyhint="search"
        autocomplete="off"
        @input="updateValue"
      />

      <button type="submit" class="search-input__submit" aria-label="搜索">
        <Search :size="16" stroke-width="1.8" />
      </button>
    </div>
  </form>
</template>

<style scoped>
.search-input {
  display: flex;
  min-width: 0;
  width: 100%;
  min-height: 62px;
  align-items: center;
  gap: 4px;
  color: var(--app-text-main);
}

.search-input__box {
  display: flex;
  min-width: 0;
  height: 40px;
  flex: 1 1 auto;
  align-items: center;
  gap: 8px;
  padding: 0 12px 0 20px;
  border: 0;
  border-radius: 46px;
  background: var(--app-surface-hover);
  transition:
    background-color 0.18s ease;
}

.search-input__box:focus-within {
  background: var(--app-surface-hover-strong);
}

.search-input__back {
  display: inline-flex;
  width: 24px;
  height: 24px;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0;
  appearance: none;
  border-radius: 999px;
  background: transparent;
  color: var(--app-text-main);
  cursor: pointer;
  opacity: 0.82;
  outline: none;
  transition:
    opacity var(--app-interaction-duration) var(--app-interaction-ease),
    transform var(--app-interaction-duration) var(--app-interaction-ease);
}

.search-input__back:hover,
.search-input__back:focus-visible {
  opacity: 1;
  transform: scale(var(--app-interaction-object-scale-hover));
}

.search-input__back:active {
  transform: scale(var(--app-interaction-object-scale-active));
}

.search-input__field {
  min-width: 0;
  flex: 1 1 auto;
  border: 0;
  appearance: none;
  background: transparent;
  color: var(--app-text-main);
  font: inherit;
  font-size: 15px;
  line-height: 22px;
  outline: none;
}

.search-input__field::placeholder {
  color: var(--app-text-muted);
}

.search-input__field::-webkit-search-cancel-button {
  display: none;
}

.search-input__submit {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  border: 0;
  appearance: none;
  cursor: pointer;
  outline: none;
  transition:
    background-color var(--app-interaction-duration) var(--app-interaction-ease),
    color var(--app-interaction-duration) var(--app-interaction-ease),
    transform var(--app-interaction-duration) var(--app-interaction-ease);
}

.search-input__submit {
  width: 24px;
  height: 24px;
  padding: 0;
  border-radius: 999px;
  background: transparent;
  color: var(--app-text-muted);
}

.search-input__submit:hover,
.search-input__submit:focus-visible {
  background: var(--app-state-layer-hover);
  color: var(--app-text-main);
  transform: scale(var(--app-interaction-object-scale-hover));
}

.search-input__submit:active {
  transform: scale(var(--app-interaction-object-scale-active));
}

@media (max-width: 640px) {
  .search-input__box {
    height: 38px;
    padding-left: 16px;
  }
}
</style>
